import { useSession } from "@/app/session";
import { useDriverCompanyName } from "@/features/companies/hooks/use-driver-company";
import { navItemsFor } from "@/components/layout/nav-items";
import { ShellFrame } from "@/components/layout/shell-frame";
import { FullScreenLoadingState } from "@/components/data/loading-state";

function userLabel(session: ReturnType<typeof useSession>, driverCompanyName: string | null | undefined): string {
  if (session.status === "company") {
    return session.company.name;
  }
  if (session.status === "admin") {
    return "Admin";
  }
  if (session.status === "driver") {
    return driverCompanyName ?? "Driver";
  }
  return "";
}

/** A driver's sidebar shows their company as the name, with their role underneath. */
function userDescription(session: ReturnType<typeof useSession>, driverCompanyName: string | null | undefined) {
  return session.status === "driver" && driverCompanyName ? "Driver" : undefined;
}

/**
 * Authenticated app shell — built on the real shadcn Sidebar primitive
 * (`components/ui/sidebar.tsx`): collapsible-to-icons on desktop, a Sheet
 * drawer on mobile, both handled internally by the primitive rather than
 * two hand-rolled layouts. See MIGRATION.md for the redesign notes.
 */
export function AppShell() {
  const session = useSession();
  const driverCompanyName = useDriverCompanyName(session.status === "driver" ? session.email : "");

  if (session.status === "loading" || session.status === "unauthenticated") {
    return <FullScreenLoadingState />;
  }

  if (session.status === "super_admin") {
    // Platform admins use the console on the admin address, never the tenant app
    return (
      <div className="flex min-h-dvh items-center justify-center p-4 text-center text-muted-foreground">
        This account signs in at the platform console.
      </div>
    );
  }

  return (
    <ShellFrame
      items={navItemsFor(session)}
      userLabel={userLabel(session, driverCompanyName)}
      userDescription={userDescription(session, driverCompanyName)}
      userEmail={session.email}
    />
  );
}
